import React, { useState } from 'react';
import PropTypes from 'prop-types';
import FormTextField from '../../../../antd/form/FormTextField/FormTextField.jsx';
import FormSelect from '../../../../antd/form/FormSelect/FormSelect.jsx';
import FormSwitch from '../../../../antd/form/FormSwitch/FormSwitch.jsx';
import FormButton from '../../../../antd/form/FormButton/FormButton.jsx';
import { CollapseBlock } from '../../../../common/index.jsx';
import {
  ButtonContainer,
  InputContainer,
} from '../../../../common/form/FormElements.jsx';
import ResponseCollectionDetailView from './ResponseCollectionDetailView.jsx';
import { Alert, Col, Form as AntdForm, Row } from 'antd';
import Form from '../../../../antd/form/Form/Form.jsx';
import { useTranslation } from 'react-i18next';
import { IconButton } from '../../../../common/controls/Controls.jsx';
import { CopyOutlined, DeleteFilled, PlusOutlined } from '@ant-design/icons';
import { required } from '../../../../antd/form/FormValidation.js';
import FormButtonsContainer from '../../../../antd/form/FormButtonsContainer/FormButtonsContainer.jsx';
import _ from 'lodash';
import { transformToUrl } from '../../../../../utils/helpers/CommonHelper.jsx';
import { routes } from '../../../../../utils/helpers/ApiClient.jsx';
import { useToastMessages } from '../../../../../hooks/useToastMessages.jsx';

const pollUrl = (slug) =>
  `${window.location.protocol}//${window.location.hostname}/${routes.polls}/${slug}`;

const PollForm = ({
  initialValues,
  responseCollections,
  onSubmit,
  alreadyVoted,
  canEditAnswered,
  saveDraft,
}) => {
  const { t } = useTranslation();
  const [questionsSet, setQuestionsSet] = useState(
    initialValues.questions.map((item) => item.response_collection_id),
  );
  const [form] = AntdForm.useForm();
  const { successMessage, errorMessage } = useToastMessages();

  // Only active collections are offered. A question that already uses an inactive
  // collection still shows its name, resolved by labelRender from all collections.
  const collectionOptions = responseCollections
    .filter((item) => item.is_active)
    .map((item) => ({ ...item, label: item.name }));
  const collectionNames = Object.fromEntries(
    responseCollections.map((item) => [item.value, item.name]),
  );

  // Answered polls are read-only, except for polls administrators who may edit them
  // with restrictions protecting the recorded responses.
  const isReadOnly = alreadyVoted && !canEditAnswered;
  const watchedQuestions =
    AntdForm.useWatch('questions', { form, preserve: true }) ??
    initialValues.questions;
  const isAnsweredQuestion = (fieldName) =>
    alreadyVoted &&
    watchedQuestions?.[fieldName]?.user_question_responses_count > 0;

  const pollsForUserType = [
    {
      value: 1,
      label: t('Študenti'),
    },
    {
      value: 2,
      label: t('Učitelia'),
    },
  ];

  const saveDraftFunction = () => {
    // all values incl. question ids and response counts, which have no form field
    saveDraft(form.getFieldsValue(true));
  };

  const defaultQuestion = {
    id: undefined,
    response_collection_id: responseCollections.find((item) => item.is_active)
      ?.value,
    poll_id: undefined,
    name: undefined,
    slug: undefined,
    response_combobox: undefined,
    user_question_responses: undefined,
    response_collection: [
      {
        id: undefined,
        name: undefined,
        responses: [
          {
            id: undefined,
            name: undefined,
          },
        ],
      },
    ],
  };
  // The slug field shows the full poll URL. A draft may already contain the full URL,
  // so only its last segment is taken to avoid nesting it into the URL again.
  const formInitialValues = {
    ...initialValues,
    slug: initialValues.slug
      ? pollUrl(transformToUrl(initialValues.slug.split('/').pop()))
      : '',
  };
  return (
    // <div className="poll-form-component">
    <div className="poll-form-component">
      <Form
        // onFinish values contain only fields with an input, all values are needed
        // so that question ids are kept and disabled fields are saved unchanged
        onFinish={() => onSubmit(form.getFieldsValue(true))}
        initialValues={formInitialValues}
        form={form}
        layout="vertical"
        style={{ margin: '10px' }}
      >
        <CollapseBlock>
          {isReadOnly && (
            <Alert
              type="warning"
              // rovnaký text je použitý aj v PollsPage.jsx
              title={t(
                'Nie je možné upraviť anketu, ktorá má zaznamenané odpovede.',
              )}
              style={{ marginBottom: 10 }}
            />
          )}
          {alreadyVoted && canEditAnswered && (
            <Alert
              type="warning"
              showIcon
              title={t('Anketa má zaznamenané odpovede.')}
              description={t(
                'Ako administrátor ankiet ju môžete upraviť. Otázky, ktoré už majú odpovede, nie je možné vymazať ani im zmeniť kolekciu odpovedí. Pri zmene názvu sa zmení aj URL ankety, už rozposlané odkazy prestanú fungovať.',
              )}
              style={{ marginBottom: 10 }}
            />
          )}
          <FormTextField
            label={t('Názov ankety')}
            name="name"
            rules={[required(t)]}
            onChange={() => {
              form.setFieldValue(
                'slug',
                pollUrl(transformToUrl(form.getFieldValue('name'))),
              );
            }}
            disabled={isReadOnly}
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <div style={{ flexGrow: 1 }}>
              <FormTextField label={t('URL')} name="slug" disabled />
            </div>
            <IconButton
              icon={<CopyOutlined />}
              tooltip={t('Skopírovať odkaz')}
              onClick={() => {
                const fullUrl = form.getFieldValue('slug');

                // Copy to clipboard
                navigator.clipboard
                  .writeText(fullUrl)
                  .then(() => {
                    successMessage({
                      message: `${t('URL adresa skopírovaná')}. ${t('URL adresa bola skopírovaná do schránky.')}`,
                    });
                  })
                  .catch(() => {
                    errorMessage({
                      message: `${t('Kopírovanie zlyhalo.')}. ${t('Kopírovanie URL adresy do schránky zlyhalo.')}`,
                    });
                  });
              }}
            />
          </div>
        </CollapseBlock>

        <CollapseBlock collapsible title={t('Otázky v ankete')}>
          <InputContainer>
            <AntdForm.List name="questions">
              {(fields, { add, remove }) => {
                return (
                  <>
                    {fields.map((field, index) => (
                      <div key={'q' + index}>
                        <FormTextField
                          label={t('Otázka ankety č.') + `${index + 1}`}
                          key={[field.name, 'name']}
                          name={[field.name, 'name']}
                          rules={[required(t)]}
                          className="min-bottom-margin"
                          disabled={isReadOnly}
                        />
                        <div className="poll-results-wrapper">
                          <FormSelect
                            className="poll-select"
                            name={[field.name, 'response_collection_id']}
                            label={
                              t('Kolekcia odpovedí k otázke č.') +
                              ' ' +
                              (index + 1)
                            }
                            key={'qu' + index}
                            disabled={
                              isReadOnly || isAnsweredQuestion(field.name)
                            }
                            options={collectionOptions}
                            labelRender={({ value, label }) =>
                              label ?? collectionNames[value] ?? value
                            }
                            rules={[required(t)]}
                            onChange={(value) => {
                              setQuestionsSet(
                                _.set(
                                  _.cloneDeep(questionsSet),
                                  `[${index}]`,
                                  value,
                                ),
                              );
                            }}
                          />

                          <ResponseCollectionDetailView
                            collectionData={
                              responseCollections.find(
                                (rec) => rec.value === questionsSet[index],
                              )
                                ? responseCollections.find(
                                    (rec) => rec.value === questionsSet[index],
                                  ).response_collection
                                : []
                            }
                            saveDraft={saveDraftFunction}
                            isLocked={
                              isReadOnly || isAnsweredQuestion(field.name)
                            }
                          />
                          <div style={{ flexGrow: 1 }}></div>
                          {questionsSet.length > 1 &&
                            !isReadOnly &&
                            !isAnsweredQuestion(field.name) && (
                              <div>
                                <IconButton
                                  onClick={() => {
                                    setQuestionsSet(
                                      questionsSet.filter(
                                        (_, i) => index !== i,
                                      ),
                                    );
                                    remove(field.name);
                                  }}
                                  icon={<DeleteFilled />}
                                  tooltip={t('Vymazať')}
                                />
                              </div>
                            )}
                        </div>
                      </div>
                    ))}
                    {!isReadOnly && (
                      <ButtonContainer noSpace>
                        <IconButton
                          onClick={() => {
                            setQuestionsSet([
                              ...questionsSet,
                              defaultQuestion.response_collection_id,
                            ]);
                            add(defaultQuestion);
                          }}
                          icon={<PlusOutlined />}
                          tooltip={t('Pridať otázku')}
                        />
                      </ButtonContainer>
                    )}
                  </>
                );
              }}
            </AntdForm.List>
          </InputContainer>
        </CollapseBlock>

        <CollapseBlock collapsible title={t('Ďalšie nastavenia')}>
          <FormSelect
            label={t('Anketa určená pre')}
            name={'polls_users_type'}
            placeholder={t(
              'Vyberte skupinu / skupiny používateľov pre ktorých je táto anketa určená',
            )}
            options={pollsForUserType}
            rules={[required(t)]}
            mode={'multiple'}
            disabled={isReadOnly}
          />

          <Row>
            <Col span={12}>
              <FormSwitch
                label={t('Zobraziť výsledky hlasovania')}
                name={'show_results'}
              />
            </Col>
            <Col span={12}>
              <FormSwitch label={t('Aktívna')} name={'active'} />
            </Col>
          </Row>
        </CollapseBlock>

        <FormButtonsContainer>
          <FormButton submitOnClick={true} submitKey={'polls'}>
            {t('Uložiť')}
          </FormButton>
        </FormButtonsContainer>
      </Form>
    </div>
  );
};

PollForm.propTypes = {
  initialValues: PropTypes.any,
  responseCollections: PropTypes.array,
  onSubmit: PropTypes.func,
  alreadyVoted: PropTypes.bool,
  canEditAnswered: PropTypes.bool,
  saveDraft: PropTypes.func,
};

export default PollForm;
